"use client";

import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/shadcn/card";
import { ScrollArea } from "@/components/shadcn/scroll-area";
import { Separator } from "@/components/shadcn/separator";
import { secondsToHMS } from "@/lib/utils";
import type { Videos } from "@prisma/client";
import { Clock3, ListVideo } from "lucide-react";
import { SeriesSidebarVideo } from "../series-sidebar-video";

type VideoDetailPlaylistProps = {
    seriesTitle: string;
    videos: Videos[];
    currentVideoId?: number;
    isPremium: boolean | undefined;
    isLoggedIn: boolean;
    onSelect: (video: Videos) => void;
};

export function VideoDetailPlaylist({
    seriesTitle,
    videos,
    currentVideoId,
    isPremium,
    isLoggedIn,
    onSelect,
}: VideoDetailPlaylistProps) {
    const totalDuration = secondsToHMS(
        videos.reduce((total, video) => total + (video.VideoLength ?? 0), 0),
        true,
    );
    const currentVideoIndex = videos.findIndex(
        (video) => video.Id === currentVideoId,
    );
    const currentVideoPosition =
        currentVideoIndex >= 0 ? currentVideoIndex + 1 : 1;

    return (
        <div className="relative min-w-0 flex-1">
            <Card className="flex min-h-0 flex-col lg:absolute lg:inset-0">
                <CardHeader>
                    <CardTitle>{seriesTitle}</CardTitle>
                    <CardDescription className="flex flex-wrap gap-6">
                        <span className="flex items-center gap-1">
                            <Clock3 aria-hidden="true" />
                            {totalDuration}
                        </span>
                        <span className="flex items-center gap-1">
                            <ListVideo aria-hidden="true" />
                            {currentVideoPosition}/{videos.length}
                        </span>
                    </CardDescription>
                </CardHeader>

                <CardContent className="flex min-h-0 flex-1 flex-col gap-3">
                    <Separator />
                    <ScrollArea className="h-0 flex-1">
                        <div className="flex flex-col gap-2 pr-3">
                            {videos.map((video, index) => (
                                <SeriesSidebarVideo
                                    key={video.Id}
                                    video={video}
                                    isCurrent={currentVideoId === video.Id}
                                    isLocked={
                                        !isPremium && (!isLoggedIn || index > 1)
                                    }
                                    onSelect={onSelect}
                                />
                            ))}
                        </div>
                    </ScrollArea>
                </CardContent>
            </Card>
        </div>
    );
}
